/**
 * OperatorToolbar — Phase 1B-I (2026-05-29 follow-up).
 *
 * Replaces the operator's previous right-side stacked column (CameraControl
 * + SnapshotButton + InRoomAnnotationToolbar + WhiteboardButton) with a
 * single bottom-center floating glass pill, mirroring the field-side
 * FieldToolbar.
 *
 * Buttons (left → right):
 *   • Switch camera    — sends camera.switch to the field user
 *   • Torch            — sends torch.toggle to the field user
 *   • Zoom             — opens a small slider popover; debounced
 *                        camera.zoom commands to the field
 *   • Capture          — sends capture.snapshot (mint emphasis)
 *   • Freeze + collab  — capture.snapshot then auto-open collab annotator
 *   • Annotate         — opens a tool/color/clear popover; the chosen
 *                        tool is the live annotation tool everywhere
 *                        AnnotationOverlay is mounted
 *   • Whiteboard       — opens the WhiteboardPanel overlay
 *
 * The component owns its own CommandBus instance (built from the LiveKit
 * room context) so it can issue field commands directly. The capture
 * mutation duplicates the same logic as the old SnapshotButton; we keep
 * it inline rather than extracting because the two paths (with/without
 * collab) only differ by one flag.
 *
 * Mic / Cam / Share / Leave are left in RoomLayout's existing
 * MediaControls bar at the very bottom — those are LiveKit standard
 * controls and operators are used to them where they are.
 *
 * Multi-party targeting (backlog §9.4, 2026-09-08): every field
 * command below (switch/torch/zoom/capture/freeze) shares ONE
 * `useFieldParticipantSelection` — with 2+ field participants, a
 * compact `FieldParticipantPicker` floats above the pill so the
 * operator explicitly chooses who these commands address, instead of
 * the old singleton `.find()` that silently retargeted whichever
 * participant LiveKit happened to return first.
 */
import {
  useParticipants,
  useRoomContext,
} from "@livekit/components-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FieldParticipantPicker } from "@/components/FieldParticipantPicker";
import { PermissionGate } from "@/components/PermissionGate";
import { ApiPaths, api } from "@/lib/api";
import {
  ANNOTATION_COLORS,
  useAnnotationStore,
  type ToolKind,
} from "@/lib/annotation";
import { clearAnnotations } from "@/lib/annotationSync";
import {
  type Capabilities,
  useCapabilitiesStore,
  type FacingMode,
} from "@/lib/cameraCommands";
import {
  CommandBus,
  type EnvelopeIdentity,
  makeLiveKitTransport,
} from "@/lib/commandBus";
import { emitCrossSideSound } from "@/lib/crossSideSounds";
import { openCollabAnnotator } from "@/lib/evidenceView";
import { useFieldParticipantSelection } from "@/lib/fieldParticipants";
import { useRecordingStore } from "@/lib/recording";
import { useSessionBus } from "@/lib/sessionBus";
import type { Session } from "@/lib/types";
import { useParticipantRoleLookup } from "@/lib/useParticipantRoleLookup";
import { useWhiteboardPanelStore } from "@/lib/whiteboardPanelStore";
import { CaptureMetadataModal, type CaptureKind } from "@/modules/evidence";
import { WhiteboardPanel } from "@/modules/whiteboard/WhiteboardPanel";


type Props = { sessionId: string };

type CaptureAck = {
  ok: boolean;
  evidence_id?: string;
  sha256?: string;
  status?: string;
  error?: string;
};


export function OperatorToolbar({ sessionId }: Props) {
  const { t } = useTranslation();
  const room = useRoomContext();
  const participants = useParticipants();
  const qc = useQueryClient();
  const capabilities = useCapabilitiesStore((s) => s.capabilities);
  const setCapabilities = useCapabilitiesStore((s) => s.set);
  const wbBus = useSessionBus((s) => s.bus);
  const recordingActive = useRecordingStore((s) => s.active);
  const recordingPaused = useRecordingStore((s) => s.paused);

  const [bus, setBus] = useState<CommandBus | null>(null);
  const [busyCmd, setBusyCmd] = useState<
    null
    | "switch"
    | "torch"
    | "zoom"
    | "capture"
    | "freeze"
    | "pause"
    | "resume"
  >(null);
  const [err, setErr] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [annOpen, setAnnOpen] = useState(false);
  // wbOpen lives in a zustand store so the embed-iframe host bridge
  // can flip it from outside the React tree. See lib/whiteboardPanelStore.
  const wbOpen = useWhiteboardPanelStore((s) => s.open);
  const setWbOpen = useWhiteboardPanelStore((s) => s.setOpen);
  // Phase 1B-I — after a successful capture / freeze / whiteboard save,
  // hold the new Evidence id + its kind so the metadata modal can mount
  // and let the operator attach a custom title + notes + keywords.
  // Cleared on modal close.
  const [metadataTarget, setMetadataTarget] = useState<
    { evidenceId: string; kind: CaptureKind } | null
  >(null);
  const [zoomLevel, setZoomLevel] = useState<number | null>(null);
  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The field user every command below targets. Filters out
  // Egress/Ingress/SIP/Agent bots (addressing one would time out as
  // "command failed"), and — once there's more than one field
  // participant — lets the operator pick who via the floating
  // FieldParticipantPicker rather than silently guessing.
  const {
    fieldParticipants,
    selected: fieldParticipant,
    selectedIdentity,
    setSelectedIdentity,
    showPicker: showFieldPicker,
  } = useFieldParticipantSelection(participants);
  const { byParticipantIdName } = useParticipantRoleLookup(sessionId);

  // Build bus + listen for inbound capabilities.
  useEffect(() => {
    if (!room) return;
    const transport = makeLiveKitTransport(room, sessionId);
    const self: EnvelopeIdentity = {
      kind: "user",
      id: room.localParticipant.identity,
    };
    const created = new CommandBus(transport, sessionId, self);
    setBus(created);

    const off = created.on<Capabilities, void>(
      "presence/capabilities",
      ({ payload }) => {
        setCapabilities(payload);
        setTorchOn(false); // every new track boots torch off
        if (payload.zoom) {
          setZoomLevel(payload.zoom.current ?? payload.zoom.min);
        } else {
          setZoomLevel(null);
        }
      },
    );

    return () => {
      off();
      created.dispose();
      setBus(null);
    };
  }, [room, sessionId, setCapabilities]);

  const sendCmd = useCallback(
    async <T,>(
      type: "camera.switch" | "torch.toggle" | "camera.zoom",
      payload: unknown,
      label: "switch" | "torch" | "zoom",
    ): Promise<T | null> => {
      if (!bus || !fieldParticipant) {
        setErr(t("ui.operatorToolbar.fieldNotConnected"));
        return null;
      }
      setBusyCmd(label);
      setErr(null);
      try {
        const ack = await bus.request<unknown, T>({
          topic: "command",
          type,
          payload,
          to: { kind: "participant", id: fieldParticipant.identity },
          to_identities: [fieldParticipant.identity],
          timeout_ms: 8_000,
        });
        // Type narrowing — ack shape from the lib has .ok
        const a = ack as unknown as { ok: boolean; reason?: string };
        if (!a.ok) setErr(a.reason ?? t("ui.operatorToolbar.commandFailed"));
        return ack;
      } catch (e) {
        setErr((e as Error).message);
        return null;
      } finally {
        setBusyCmd(null);
      }
    },
    [bus, fieldParticipant, t],
  );

  const onSwitch = async () => {
    const ack = (await sendCmd<{ ok: boolean; facing?: FacingMode; reason?: string }>(
      "camera.switch",
      { facing: "toggle" },
      "switch",
    )) as { ok: boolean; facing?: FacingMode } | null;
    if (ack?.ok && capabilities) {
      setCapabilities({ ...capabilities, facing: ack.facing });
    }
  };

  const onTorch = async () => {
    const ack = (await sendCmd<{ ok: boolean; on?: boolean; reason?: string }>(
      "torch.toggle",
      { on: "toggle" },
      "torch",
    )) as { ok: boolean; on?: boolean } | null;
    if (ack?.ok) setTorchOn(!!ack.on);
  };

  const onZoomSlide = (next: number) => {
    setZoomLevel(next);
    if (zoomDebounceRef.current) {
      clearTimeout(zoomDebounceRef.current);
    }
    zoomDebounceRef.current = setTimeout(() => {
      void sendCmd("camera.zoom", { level: next }, "zoom");
    }, 200);
  };

  // Pause / resume — broadcast the lifecycle envelope so the field's
  // FieldSystemHandler updates its store + plays the chime. Mirrors
  // RecordingControl.PauseButton / ResumeButton's mutations so both
  // surfaces stay coherent; the underlying backend writes the segment
  // metadata either way.
  const pauseRec = useMutation({
    mutationFn: async () => {
      setBusyCmd("pause");
      const res = await api.post<Session>(
        ApiPaths.sessionRecordingPause(sessionId),
      );
      return res.data;
    },
    onSuccess: () => {
      const at = Date.now();
      useRecordingStore.getState().pause(at);
      void wbBus
        ?.send({
          topic: "system",
          type: "recording.paused",
          payload: { at },
        })
        .catch(() => {});
      void qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: (e) => setErr((e as Error).message),
    onSettled: () => setBusyCmd(null),
  });
  const resumeRec = useMutation({
    mutationFn: async () => {
      setBusyCmd("resume");
      const res = await api.post<Session>(
        ApiPaths.sessionRecordingResume(sessionId),
      );
      return res.data;
    },
    onSuccess: () => {
      const at = Date.now();
      useRecordingStore.getState().resume(at);
      void wbBus
        ?.send({
          topic: "system",
          type: "recording.resumed",
          payload: { at },
        })
        .catch(() => {});
      void qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: (e) => setErr((e as Error).message),
    onSettled: () => setBusyCmd(null),
  });

  // Capture — duplicates SnapshotButton's mutation logic so we can drive
  // both Capture and Freeze+collab from one toolbar without reaching
  // into the old component tree.
  const capture = useMutation({
    mutationFn: async (input: { withCollab: boolean }): Promise<CaptureAck & { _withCollab: boolean }> => {
      if (!bus) throw new Error(t("ui.operatorToolbar.busNotReady"));
      if (!fieldParticipant) throw new Error(t("ui.operatorToolbar.noFieldParticipant"));
      setBusyCmd(input.withCollab ? "freeze" : "capture");
      const ack = await bus.request<unknown, CaptureAck>({
        topic: "command",
        type: "capture.snapshot",
        payload: { kind: "snapshot", mime: "image/png" },
        to: { kind: "participant", id: fieldParticipant.identity },
        to_identities: [fieldParticipant.identity],
        timeout_ms: 30_000,
      });
      if (!ack.ok) throw new Error(ack.error ?? t("ui.operatorToolbar.captureDeclined"));
      return { ...ack, _withCollab: input.withCollab };
    },
    onSuccess: async (ack) => {
      // Shutter on both sides — local play + bus broadcast.
      void emitCrossSideSound("shutter");
      void qc.invalidateQueries({ queryKey: ["session-chat", sessionId] });
      void qc.invalidateQueries({ queryKey: ["session-evidence", sessionId] });
      // Pop the metadata modal so the operator can name + annotate it.
      // The chat card already auto-posted with empty body; the PATCH
      // syncs the body when they save. The Freeze + collab path opens
      // the metadata modal AND the collab annotator — both surface on
      // the same evidence; collab is the heavier overlay so it stacks
      // on top.
      if (ack.evidence_id) {
        setMetadataTarget({ evidenceId: ack.evidence_id, kind: "snapshot" });
      }
      if (ack._withCollab && ack.evidence_id) {
        openCollabAnnotator(bus, ack.evidence_id);
      }
    },
    onError: (e) => setErr((e as Error).message),
    onSettled: () => setBusyCmd(null),
  });

  // Capability derived disables.
  const cameraCount = capabilities?.camera_count ?? 0;
  const switchDisabled =
    !fieldParticipant || (cameraCount > 0 && cameraCount < 2);
  const torchSupported = capabilities?.torch ?? false;
  const torchDisabled = !fieldParticipant || (capabilities !== null && !torchSupported);
  const zoom = capabilities?.zoom ?? null;
  const zoomDisabled = !fieldParticipant || zoom === null;
  const captureDisabled = !bus || !fieldParticipant || capture.isPending;

  return (
    <>
      {err && (
        <div
          role="alert"
          className="pointer-events-none absolute left-1/2 top-20 z-30 -translate-x-1/2 rounded-md bg-cvp-danger/85 px-3 py-1.5 text-xs text-white shadow-lg backdrop-blur"
        >
          {err}
        </div>
      )}

      {showFieldPicker && (
        <PermissionGate anyOf={["evidence.create", "session.command.issue"]}>
          <div
            data-testid="operator-toolbar-field-picker"
            className="pointer-events-auto absolute bottom-36 left-1/2 z-30 -translate-x-1/2 rounded-md border border-white/15 bg-black/70 px-3 py-1.5 shadow-xl backdrop-blur-md"
          >
            <FieldParticipantPicker
              participants={fieldParticipants}
              selectedIdentity={selectedIdentity}
              onChange={setSelectedIdentity}
              displayNameByParticipantId={byParticipantIdName}
              className="flex items-center gap-2 text-xs text-white"
            />
          </div>
        </PermissionGate>
      )}

      <PermissionGate anyOf={["evidence.create", "session.command.issue", "session.annotation.create"]}>
        <div
          data-testid="operator-toolbar"
          data-cvp-role="operator-toolbar"
          className="pointer-events-auto absolute bottom-20 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-white/15 bg-black/55 px-3 py-2 shadow-2xl backdrop-blur-md"
        >
          {/* Field hardware commands. */}
          <PermissionGate perm="session.command.issue">
            <ToolButton
              onClick={onSwitch}
              disabled={switchDisabled || busyCmd === "switch"}
              title={t("ui.operatorToolbar.switchCamera")}
              data-testid="op-switch-camera"
            >
              <FlipIcon />
            </ToolButton>
            <ToolButton
              active={torchOn}
              onClick={onTorch}
              disabled={torchDisabled || busyCmd === "torch"}
              title={t("ui.operatorToolbar.torch")}
              data-testid="op-torch"
            >
              <TorchIcon />
            </ToolButton>
            <div className="relative">
              <ToolButton
                onClick={() => setZoomOpen((v) => !v)}
                disabled={zoomDisabled || busyCmd === "zoom"}
                active={zoomOpen}
                title={t("ui.operatorToolbar.zoom")}
                data-testid="op-zoom"
              >
                <ZoomIcon />
              </ToolButton>
              {zoomOpen && zoom && zoomLevel !== null && (
                <div
                  className="absolute bottom-full left-1/2 mb-2 flex w-56 -translate-x-1/2 items-center gap-2 rounded-md border border-white/15 bg-black/85 px-3 py-2 text-xs text-white shadow-xl backdrop-blur-md"
                  data-testid="op-zoom-popover"
                >
                  <span className="shrink-0">{zoom.min.toFixed(1)}x</span>
                  <input
                    type="range"
                    min={zoom.min}
                    max={zoom.max}
                    step={zoom.step ?? 0.1}
                    value={zoomLevel}
                    onChange={(e) => onZoomSlide(Number(e.target.value))}
                    className="flex-1 accent-nb-mint"
                  />
                  <span className="w-10 shrink-0 text-right tabular-nums">
                    {zoomLevel.toFixed(1)}x
                  </span>
                </div>
              )}
            </div>
          </PermissionGate>

          <Divider />

          {/* Pause / Resume recording. Only render when a recording
              lifecycle is in flight; Start is intentionally NOT here —
              it lives in RecordingControl with the double-confirm
              gate (AGENTS.md §16). */}
          {recordingActive && (
            <PermissionGate perm="session.recording.stop">
              {recordingPaused ? (
                <ToolButton
                  emphasis
                  onClick={() => resumeRec.mutate()}
                  disabled={resumeRec.isPending}
                  title={t("ui.operatorToolbar.resumeRecording")}
                  data-testid="op-resume"
                >
                  <PlayIcon />
                </ToolButton>
              ) : (
                <ToolButton
                  onClick={() => pauseRec.mutate()}
                  disabled={pauseRec.isPending}
                  title={t("ui.operatorToolbar.pauseRecording")}
                  data-testid="op-pause"
                >
                  <PauseIcon />
                </ToolButton>
              )}
              <Divider />
            </PermissionGate>
          )}

          {/* Capture / freeze. */}
          <PermissionGate perm="evidence.create">
            <ToolButton
              emphasis
              onClick={() => capture.mutate({ withCollab: false })}
              disabled={captureDisabled}
              title={t("ui.operatorToolbar.capture")}
              data-testid="op-capture"
            >
              <ShutterIcon />
            </ToolButton>
            <ToolButton
              onClick={() => capture.mutate({ withCollab: true })}
              disabled={captureDisabled}
              title={t("ui.operatorToolbar.freezeAnnotate")}
              data-testid="op-freeze"
            >
              <FreezeIcon />
            </ToolButton>
          </PermissionGate>

          <Divider />

          {/* Annotate popover. */}
          <PermissionGate perm="session.annotation.create">
            <div className="relative">
              <ToolButton
                onClick={() => setAnnOpen((v) => !v)}
                active={annOpen}
                title={t("ui.operatorToolbar.annotate")}
                data-testid="op-annotate"
              >
                <PenIcon />
              </ToolButton>
              {annOpen && (
                <AnnotatePopover
                  sessionId={sessionId}
                  bus={bus}
                />
              )}
            </div>
          </PermissionGate>

          {/* Whiteboard. */}
          <ToolButton
            onClick={() => setWbOpen(true)}
            title={t("ui.operatorToolbar.whiteboard")}
            data-testid="op-whiteboard"
          >
            <BoardIcon />
          </ToolButton>
        </div>
      </PermissionGate>

      {wbOpen && (
        <WhiteboardPanel
          sessionId={sessionId}
          bus={wbBus}
          canSave
          onClose={() => setWbOpen(false)}
          onSaved={(savedEvidenceId) => {
            // Hand off to the metadata modal so the operator can name
            // the whiteboard before it sits silently in the chat.
            setMetadataTarget({
              evidenceId: savedEvidenceId,
              kind: "whiteboard",
            });
          }}
        />
      )}

      {metadataTarget && (
        <CaptureMetadataModal
          evidenceId={metadataTarget.evidenceId}
          sessionId={sessionId}
          kind={metadataTarget.kind}
          onClose={() => setMetadataTarget(null)}
        />
      )}
    </>
  );
}


function AnnotatePopover({
  sessionId,
  bus,
}: {
  sessionId: string;
  bus: CommandBus | null;
}) {
  const { t } = useTranslation();
  const tool = useAnnotationStore((s) => s.tool);
  const setTool = useAnnotationStore((s) => s.setTool);
  const color = useAnnotationStore((s) => s.color);
  const setColor = useAnnotationStore((s) => s.setColor);
  const [clearing, setClearing] = useState(false);
  const TOOLS: { id: ToolKind; label: string; symbol: string }[] = [
    { id: "off", label: t("ui.operatorToolbar.toolOff"), symbol: "↖" },
    { id: "pen", label: t("ui.operatorToolbar.toolPen"), symbol: "✏︎" },
    { id: "rect", label: t("ui.operatorToolbar.toolRect"), symbol: "▭" },
    { id: "ellipse", label: t("ui.operatorToolbar.toolEllipse"), symbol: "◯" },
    { id: "arrow", label: t("ui.operatorToolbar.toolArrow"), symbol: "➜" },
  ];
  return (
    <div
      className="absolute bottom-full left-1/2 mb-2 flex w-56 -translate-x-1/2 flex-col gap-2 rounded-md border border-white/15 bg-black/85 p-3 text-xs text-white shadow-xl backdrop-blur-md"
      data-testid="op-annotate-popover"
    >
      <div className="flex gap-1" role="radiogroup" aria-label={t("ui.operatorToolbar.annotationToolLabel")}>
        {TOOLS.map((t) => (
          <button
            key={t.id}
            role="radio"
            aria-checked={tool === t.id}
            aria-label={t.label}
            type="button"
            onClick={() => setTool(t.id)}
            className={`h-8 w-8 rounded-md text-sm transition ${
              tool === t.id ? "bg-nb-mint text-black" : "text-white hover:bg-white/10"
            }`}
          >
            {t.symbol}
          </button>
        ))}
      </div>
      <div className="flex gap-1" role="radiogroup" aria-label={t("ui.operatorToolbar.annotationColorLabel")}>
        {ANNOTATION_COLORS.map((c) => (
          <button
            key={c}
            role="radio"
            aria-checked={color === c}
            aria-label={t("ui.operatorToolbar.colorSwatch", { color: c })}
            type="button"
            onClick={() => setColor(c)}
            style={{ backgroundColor: c }}
            className={`h-6 w-6 rounded-full border-2 transition ${
              color === c ? "border-white" : "border-transparent"
            }`}
          />
        ))}
      </div>
      <button
        type="button"
        className="rounded-md bg-white/10 px-2 py-1 text-xs text-white transition hover:bg-white/20 disabled:opacity-40"
        disabled={clearing}
        onClick={async () => {
          setClearing(true);
          try {
            await clearAnnotations(bus, sessionId);
          } finally {
            setClearing(false);
          }
        }}
      >
        {clearing ? t("ui.operatorToolbar.clearing") : t("ui.operatorToolbar.clearAll")}
      </button>
    </div>
  );
}


function Divider() {
  return <span className="mx-0.5 h-7 w-px bg-white/15" aria-hidden />;
}


function ToolButton({
  children,
  active,
  emphasis,
  title,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  emphasis?: boolean;
}) {
  // Wrap in a `group` so we can drive both an icon-shrink animation
  // and a below-button label from the same hover. Removing the native
  // `title` from the button keeps the browser tooltip from racing the
  // floating label — the inline label is now the source of truth.
  const base =
    "flex h-11 w-11 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:transition-transform [&_svg]:duration-150 group-hover:[&_svg]:scale-90";
  const palette = emphasis
    ? "bg-nb-mint text-nb-bg hover:bg-nb-mint/90"
    : active
      ? "bg-nb-mint/85 text-nb-bg hover:bg-nb-mint"
      : "bg-white/10 text-white hover:bg-white/20";
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        className={`${base} ${palette}`}
        aria-label={title ?? undefined}
        {...rest}
      >
        {children}
      </button>
      {title && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-1/2 top-full z-40 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/90 px-2 py-0.5 text-[10px] font-medium text-white opacity-0 shadow-md transition-opacity duration-150 group-hover:opacity-100"
        >
          {title}
        </span>
      )}
    </span>
  );
}

// --- Icons — inline SVG to avoid a new dep.

function FlipIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M3 7h13l-2-2M3 7l2-2M21 17H8l2 2M21 17l-2 2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function TorchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M9 3h6l-1 4h2l-5 14 1-9H8l1-9z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}
function ZoomIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path
        d="M16 16l4.5 4.5M8 11h6M11 8v6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
function ShutterIcon() {
  // Camera body + lens (not a filled-dot inside a ring — that read as
  // "record button" in the 2026-05-29 review). Square corners and a
  // small viewfinder bump make it unambiguous as "take a photo".
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M3 8a2 2 0 012-2h2.5l1.5-2h6l1.5 2H19a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="13"
        r="3.5"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}
function FreezeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M5.6 18.4L18.4 5.6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
function PenIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M14.5 4.5l5 5L8 21H3v-5L14.5 4.5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function BoardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <rect x="3" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M8 20l4-3 4 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
      <path d="M7 5l13 7-13 7V5z" />
    </svg>
  );
}
